import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import Users from './pages/Users';
import Inventory from './pages/Inventory';
import Clients from './pages/Clients';
import Sales from './pages/Sales';
import Deliveries from './pages/Deliveries';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import Roles from './pages/Roles';
import Templates from './pages/Templates';
import ClientPortal from './pages/ClientPortal';
import DispatchQueue from './pages/DispatchQueue';
import Finance from './pages/Finance';
import PricingHistory from './pages/PricingHistory';
import Payroll from './pages/Payroll';
import Audit from './pages/Audit';
import Deployment from './pages/Deployment';
import FinancePro from './pages/FinancePro';
import SiteSalesPRO from './pages/SiteSalesPRO';
import Marketplace from './pages/Marketplace';
import Storefront from './pages/Storefront';
import StoreConfig from './pages/StoreConfig';
import WebPresence from './pages/WebPresence';
import DocConfig from './pages/DocConfig';
import Marketing from './pages/Marketing';
import CalendarPage from './pages/Calendar';
import License from './pages/License';
import SellLicense from './pages/SellLicense';
import MasterAgenda from './pages/MasterAgenda';
import Collections from './pages/Collections';
import CRM from './pages/CRM';
import CreditQueue from './pages/CreditQueue';
import BranchReports from './pages/BranchReports';
import MallConfig from './pages/MallConfig';
import MallStore from './pages/MallStore';
import Transfers from './pages/Transfers';
import Promotions from './pages/Promotions';
import Policies from './pages/Policies';
import { LicenseProvider } from './contexts/LicenseContext';
import { api } from './lib/api';
import { authService } from './lib/auth';
import './App.css';

function ClientRedirect() {
  const user = authService.getUser();
  if (user?.role_id === 'cliente') {
    return <Navigate to="/client-portal" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function App() {
  const _host = window.location.hostname;
  const _path = window.location.pathname;
  const APP_HOSTS = /(localhost|127\.0\.0\.1|emergentagent\.com|vercel\.app|salespr)/i;
  // Dominio principal de la vitrina Celular Beach: raíz = vitrina global, /slug = página de cada sucursal
  const isMallHost = _host.includes(process.env.REACT_APP_MALL_DOMAIN || 'celularbeach');
  // ¿Estamos en la raíz de un dominio de tienda (empresa)? Entonces abrimos su tienda, no el panel.
  const isStoreDomainRoot = _path === '/' && !APP_HOSTS.test(_host) && !isMallHost;
  const [booting, setBooting] = useState(isStoreDomainRoot);

  useEffect(() => {
    const host = window.location.hostname;

    // Marca dinámica por dominio: favicon, ícono PWA (manifest), título y color.
    const BRANDS = [
      { key: 'fya', match: (h) => h.includes('fyams'), title: 'FyA Multiservicios Car Express',
        favicon: '/brand/fya-favicon.png', apple: '/brand/fya-apple.png', manifest: '/brand/fya.webmanifest',
        theme: '#0a0a0c', slug: 'store' },
      { key: 'salespro', match: (h) => h.includes('salespr'), title: 'SalesPRO – Celular Beach',
        favicon: '/brand/salespro-favicon.png', apple: '/brand/salespro-apple.png', manifest: '/brand/salespro.webmanifest',
        theme: '#022C22', slug: null },
    ];
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
      el.setAttribute('href', href);
    };
    // El acceso administrativo (/lg, /login) siempre es SalesPRO; la tienda usa la marca de la empresa.
    const path = window.location.pathname;
    const isAppLogin = path.startsWith('/lg') || path.startsWith('/login');
    const salespro = BRANDS.find((b) => b.key === 'salespro');
    const brand = isAppLogin ? salespro : (BRANDS.find((b) => b.match(host)) || salespro);
    if (brand && !(isMallHost || path.startsWith('/vitrina'))) {
      document.title = brand.title;
      setLink('icon', brand.favicon);
      setLink('apple-touch-icon', brand.apple);
      setLink('manifest', brand.manifest);
      const tc = document.querySelector('meta[name="theme-color"]');
      if (tc) tc.setAttribute('content', brand.theme);
    }

    // Resolución de tienda por DOMINIO personalizado. Mientras resolvemos mostramos un loader
    // para NO parpadear al /login (evita la carrera con el ProtectedRoute).
    const boot = async () => {
      if (!isAppLogin && isStoreDomainRoot) {
        // 1) Dominio conocido: redirección INSTANTÁNEA a su tienda (sin depender del backend).
        const known = BRANDS.find((b) => b.match(host) && b.slug);
        if (known) { window.location.replace(`/tienda/${known.slug}`); return; }
        // 2) Otros dominios: resolver el slug por el backend.
        try {
          const base = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${base}/api/public/store-domain/${host}`);
          if (res.ok) {
            const j = await res.json();
            if (j.slug) { window.location.replace(`/tienda/${j.slug}`); return; }
          }
        } catch { /* si falla, mostramos el panel */ }
        setBooting(false);
      }
      try {
        await api.initializeApp();
      } catch (error) {
        console.log('SalesPRO already initialized or error:', error.response?.data);
      }
    };
    boot();
  }, [isStoreDomainRoot, isMallHost]);

  if (booting) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a0a0c', color: '#fff', gap: 18 }}>
        <div style={{ width: 44, height: 44, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
        <div style={{ fontSize: 15, opacity: 0.85 }}>Cargando tienda…</div>
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    );
  }

  return (
    <AuthProvider>
      <LicenseProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/lg" element={<Login />} />
          <Route path="/lg/:slug" element={<Login />} />
          <Route path="/login/:slug" element={<Login />} />
          <Route path="/site" element={<SiteSalesPRO />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/tienda/:slug" element={<Storefront />} />
          <Route path="/vitrina" element={<MallStore basePath="/vitrina" />} />
          <Route path="/vitrina/:slug" element={<MallStore basePath="/vitrina" />} />
          {isMallHost && <Route path="/" element={<MallStore basePath="" />} />}
          {isMallHost && <Route path="/:slug" element={<MallStore basePath="" />} />}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path={isMallHost ? undefined : '/'}
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {!isMallHost && <Route index element={<ClientRedirect />} />}
            <Route path="license" element={<License />} />
            <Route path="sell-license" element={<SellLicense />} />
            <Route path="master-agenda" element={<MasterAgenda />} />
            <Route path="collections" element={<Collections />} />
            <Route path="crm" element={<CRM />} />
            <Route path="credit-queue" element={<CreditQueue />} />
            <Route path="branch-reports" element={<BranchReports />} />
            <Route path="mall-config" element={<MallConfig />} />
            <Route path="transfers" element={<Transfers />} />
            <Route path="promotions" element={<Promotions />} />
            <Route path="policies" element={<Policies />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="companies" element={<Companies />} />
            <Route path="users" element={<Users />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="clients" element={<Clients />} />
            <Route path="sales" element={<Sales />} />
            <Route path="deliveries" element={<Deliveries />} />
            <Route path="dispatch-queue" element={<DispatchQueue />} />
            <Route path="client-portal" element={<ClientPortal />} />
            <Route path="finance" element={<Finance />} />
            <Route path="pricing-history" element={<PricingHistory />} />
            <Route path="payroll" element={<Payroll />} />
            <Route path="audit" element={<Audit />} />
            <Route path="reports" element={<Reports />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="roles" element={<Roles />} />
            <Route path="templates" element={<Templates />} />
            <Route path="deployment" element={<Deployment />} />
            <Route path="finance-pro" element={<FinancePro />} />
            <Route path="store-config" element={<StoreConfig />} />
            <Route path="web-presence" element={<WebPresence />} />
            <Route path="doc-config" element={<DocConfig />} />
            <Route path="marketing" element={<Marketing />} />
            <Route path="calendar" element={<CalendarPage />} />
          </Route>
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
      </LicenseProvider>
    </AuthProvider>
  );
}

export default App;
